import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { forgetPaymentWatermarkSummary, type PaymentWatermarkSummary } from '../paymentWatermarkSummary';
import { useCleanCopies } from '../useCleanCopies';
import { cleanCopiesMessages } from './cleanCopiesMessages';
import { CleanCopiesView } from './CleanCopiesView';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The poll for the run row, and the longest wait for it, after the payment. */
export const PAYMENT_CLEAN_COPIES_POLL_MS = 3 * 1000;
export const PAYMENT_CLEAN_COPIES_WAIT_MS = 30 * 1000;

interface PaymentCleanCopiesProps {
  /** The plain "your subscription is active" content: shown when the licence has no run. */
  children: React.ReactNode;
  /**
   * The watermark summary of before the payment (`readPaymentWatermarkSummary`), read by the page
   * at its first render. Null: the tab has none, so the page does not wait for a run.
   */
  before?: PaymentWatermarkSummary | null;
}

/**
 * The success state of the payment return page (design: trial-upsell b5): the clean copies when
 * the first paid plan of the licence started a run, else the plain success content.
 *
 * A job makes the run row after the payment, so the first answer can be "no run" for a licence
 * that gets one. When the summary of before the payment had files, or said that clean copies were
 * still to come, the page reads again each `PAYMENT_CLEAN_COPIES_POLL_MS` for at most
 * `PAYMENT_CLEAN_COPIES_WAIT_MS`. Meanwhile it says "We prepare your clean copies", but only when
 * that summary had files and promised the copies. After the wait, the plain content shows.
 */
const PaymentCleanCopiesComponent: React.FC<PaymentCleanCopiesProps> = ({ children, before = null }) => {
  const intl = useIntl();
  const [waitOver, setWaitOver] = useState(false);
  const mayStart = before !== null && (before.files > 0 || before.cleanCopiesAvailable);
  const isWaiting = mayStart && !waitOver;
  const { cleanCopies } = useCleanCopies({ pollWhileNoRunMs: isWaiting ? PAYMENT_CLEAN_COPIES_POLL_MS : 0 });
  const hasRun = cleanCopies !== null;

  useEffect(() => {
    if (!isWaiting || hasRun) {
      return undefined;
    }
    const timer = setTimeout(() => setWaitOver(true), PAYMENT_CLEAN_COPIES_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isWaiting, hasRun]);

  // The summary of before the payment did its work when the run is here or the wait ended.
  useEffect(() => {
    if (hasRun || waitOver) {
      forgetPaymentWatermarkSummary();
    }
  }, [hasRun, waitOver]);

  if (cleanCopies === null) {
    if (isWaiting && before !== null && before.files > 0 && before.cleanCopiesAvailable) {
      return (
        <div className={`${styles.modal} ${styles.cleanCard}`} data-testid="clean-copies-preparing">
          <div>
            <h3 className={styles.h}>{intl.formatMessage(cleanCopiesMessages.preparingTitle)}</h3>
            <p className={styles.p}>{intl.formatMessage(cleanCopiesMessages.preparingBody, { count: before.files })}</p>
          </div>
        </div>
      );
    }
    return <>{children}</>;
  }
  // A run with no file (the licence had no watermarked file) has no progress to show. The cover
  // line stays: the cover editor must export a watermarked cover again.
  if (cleanCopies.total === 0) {
    return (
      <>
        {children}
        {cleanCopies.coverHasWatermark && (
          <p className={styles.fine} data-testid="clean-copies-cover">
            {intl.formatMessage(cleanCopiesMessages.coverLine)}
          </p>
        )}
      </>
    );
  }
  return <CleanCopiesView cleanCopies={cleanCopies} />;
};

export const PaymentCleanCopies = TypedMemo(PaymentCleanCopiesComponent);
